// src/lib/prorata-resolve.js — Lot 5a PRORATA (5e calque facturation V2).
//
// ⚠️ DUPLICATION CONTRÔLÉE — Pattern ADR-021.
// Ce fichier est la version ESM ; le miroir CJS est lib/prorata-resolve.js.
// Toute modification DOIT être appliquée AUX DEUX fichiers (test de parité
// prorata-resolve-cjs-parity.test.js — verrou anti-dérive).
//
// CHEMIN ARGENT — invariant NON-NÉGOCIABLE : Σ des parts == montantTotal au
// centime EXACT (le dernier mois absorbe le résidu d'arrondi). Toute incohérence
// (gel, mois fantôme, montant négatif, dénominateur ≤ 0) → return null →
// l'appelant laisse la résa ENTIÈRE sur son mois naturel (fail-closed : jamais
// de perte ni de duplication d'argent).
//
// MODÈLE DE DONNÉES (data._prorataSplits[lid][code]) :
//   { mode:'auto', excluded:['2026-09', ...] }   // prorata nuits réelles, mois
//                                                 //   exclus retirés du dénom.
//   { mode:'manual', amounts:{'2026-06':800, ...} } // montants figés, renormalisés
//                                                    //   si Σ ≠ total.

import { nightsInMonth } from './resa-invoice-month'

// round2 : arrondi 2 décimales (centime). Source unique du module.
function round2(n) {
  return Math.round(n * 100) / 100
}

// ── resolveProrataParts(resa, montantTotal, spec, monthsTouched, frozenMonths) ──
// Répartit `montantTotal` (SCALAIRE déjà résolu APRÈS montantOverride par
// l'appelant) sur les mois `monthsTouched` selon `spec`. Retourne { [mk]: part }
// avec Σ parts == montantTotal au centime, ou `null` si la proratisation est
// impossible / incohérente (fail-closed).
//
// @param {object} resa — objet résa entier (pour nightsInMonth en mode auto).
// @param {number} montantTotal — montant final APRÈS override. Invariant
//   d'entrée : montantTotal === round2(montantTotal). On ne re-dérive JAMAIS
//   d'override ici.
// @param {object} spec — { mode:'auto', excluded?:string[] }
//                      | { mode:'manual', amounts:{[mk]:number} }
// @param {string[]} monthsTouched — mk où nightsInMonth(resa, mk) > 0 (fournis
//   par l'appelant — source unique du périmètre temporel).
// @param {Set<string>|string[]} frozenMonths — sous-ensemble de monthsTouched
//   qui sont GELÉS (facturés). Fourni par l'appelant.
// @param {object} [ctx] — { lid, code } pour les warns (code stable seul, 0 PII).
// @returns {{[mk]:number}|null}
export function resolveProrataParts(resa, montantTotal, spec, monthsTouched, frozenMonths, ctx = {}) {
  // 1. Garde de base.
  if (!Number.isFinite(montantTotal)) return null
  if (!Array.isArray(monthsTouched) || monthsTouched.length === 0) return null
  if (!spec || typeof spec !== 'object') return null

  // Normalise frozenMonths en Set (accepte Set OU array).
  const frozenSet =
    frozenMonths && typeof frozenMonths.has === 'function'
      ? frozenMonths
      : new Set(Array.isArray(frozenMonths) ? frozenMonths : [])

  // 2. BLOQUANT B1 — gel : si AU MOINS un mois touché est gelé, on ne proratise
  //    PAS (on ne réécrit jamais un mois déjà facturé). Fail-closed → null.
  for (const mk of monthsTouched) {
    if (frozenSet.has(mk)) {
      // eslint-disable-next-line no-console
      console.warn('proprio-stats.prorata_frozen_month', {
        lid: ctx.lid,
        code: ctx.code,
      })
      return null
    }
  }

  // 3. Ordre chronologique (lexico === chrono sur 'YYYY-MM').
  const months = [...monthsTouched].sort()

  const weights = {}
  const keptMonths = []
  let sumW = 0

  if (spec.mode === 'manual') {
    const amounts = spec.amounts
    if (!amounts || typeof amounts !== 'object') return null
    const touchedSet = new Set(monthsTouched)
    // BLOQUANT B3 — mois fantôme : tout mk de amounts hors monthsTouched → null.
    for (const mk of Object.keys(amounts)) {
      if (!touchedSet.has(mk)) return null
      const a = Number(amounts[mk])
      // m3 — montant négatif → null (incohérence, fail-closed).
      if (Number.isFinite(a) && a < 0) return null
    }
    // weights[mk] = amount fini > 0 pour mk ∈ months. sumW = Σ weights.
    for (const mk of months) {
      const a = Number(amounts[mk])
      if (Number.isFinite(a) && a > 0) {
        weights[mk] = a
        keptMonths.push(mk)
        sumW += a
      }
    }
    // M3 — Σ amounts ≠ montantTotal → renormalisation (la répartition étape 7
    // divise par sumW puis multiplie par montantTotal → renormalise de fait).
    if (Math.abs(sumW - montantTotal) > 0.01) {
      // eslint-disable-next-line no-console
      console.warn('proprio-stats.prorata_renormalized', {
        lid: ctx.lid,
        code: ctx.code,
      })
    }
  } else {
    // 4. mode auto (défaut) : poids = nuits réelles par mois, mois exclus retirés
    //    du dénominateur (redistribution mécanique sur les mois restants).
    const excluded = Array.isArray(spec.excluded) ? new Set(spec.excluded) : new Set()
    for (const mk of months) {
      if (excluded.has(mk)) continue
      const w = nightsInMonth(resa, mk)
      if (w > 0) {
        weights[mk] = w
        keptMonths.push(mk)
        sumW += w
      }
    }
  }

  // 6. Garde incohérence : aucun mois retenu / poids total ≤ 0 → null
  //    (tous exclus, amounts vides, ou Σ ≤ 0). Fail-closed.
  if (keptMonths.length === 0 || sumW <= 0) return null

  // 7. Répartition + résidu (invariant Σ == montantTotal EXACT au centime).
  //    Chrono : le DERNIER mois retenu absorbe le résidu d'arrondi.
  const out = {}
  let acc = 0
  for (let i = 0; i < keptMonths.length; i++) {
    const mk = keptMonths[i]
    if (i === keptMonths.length - 1) {
      out[mk] = round2(montantTotal - acc) // absorbe le résidu (Σ == total EXACT)
    } else {
      const p = round2((montantTotal * weights[mk]) / sumW)
      out[mk] = p
      acc = round2(acc + p)
    }
  }

  // 8. return.
  return out
}

export { round2 }
